import { useState, useEffect, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  ScheduleTab, LodgingTab, TransportTab, VisaTab, TipsTab,
  WeatherTab, InsuranceTab, GalleryTab, CompanionsTab,
} from './index';
import PhotoJournalTab from './PhotoJournalTab';
import { TabSkeleton } from './TabSkeletons';
import {
  Calendar, Hotel, Car, Globe, Shield, Thermometer, Heart, Camera, UserPlus, BookOpen,
} from 'lucide-react';

/**
 * Determine if a trip is domestic (all destination countries match origin/nationality country).
 */
function isDomesticTrip(tripInput) {
  if (!tripInput) return false;
  const origin = (tripInput.origin_country || '').toLowerCase().trim();
  const nationality = (tripInput.nationality || '').toLowerCase().trim();
  const refCountry = origin || nationality;
  if (!refCountry) return false;

  const destCountries = tripInput.destination_countries?.length
    ? tripInput.destination_countries
    : tripInput.destination_country ? [tripInput.destination_country] : [];

  if (destCountries.length === 0) return false;
  return destCountries.every(dc => dc.toLowerCase().trim() === refCountry);
}

export const ItineraryTabsSection = ({
  activeTab, setActiveTab, itineraryId, generatedItinerary, tripInput, currencyProps,
  visaData, loadingVisa, weatherData, loadingWeather, insuranceData, loadingInsurance, companions,
  fetchVisaInfo, fetchWeather, fetchInsurance, fetchCompanions,
  openGallery, handleBookFlight, handleBookLodging,
  setShowInviteDialog, handleRemoveCompanion,
  onItineraryUpdate,
}) => {
  const [visitedTabs, setVisitedTabs] = useState(() => new Set([activeTab]));
  const [loadingTab, setLoadingTab] = useState(null);

  const domestic = useMemo(() => isDomesticTrip(tripInput), [tripInput]);

  useEffect(() => {
    if (visitedTabs.has(activeTab)) return;
    setLoadingTab(activeTab);
    const timer = setTimeout(() => {
      setVisitedTabs(prev => new Set([...prev, activeTab]));
      setLoadingTab(null);
    }, 500);
    return () => clearTimeout(timer);
  }, [activeTab, visitedTabs]);

  const loaded = (tab) => visitedTabs.has(tab);

  const renderTab = (tab, content) =>
    loadingTab === tab ? <TabSkeleton tab={tab} /> : loaded(tab) ? content : null;

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="itinerary-tabs">
      <TabsList className="w-full justify-start bg-white/95 backdrop-blur-xl border border-white/30 mb-6 shadow-xl rounded-xl p-1 flex-wrap">
        <TabsTrigger value="schedule" className="data-[state=active]:bg-ocean data-[state=active]:text-white data-[state=active]:shadow-lg rounded-lg transition-all duration-300" data-testid="tab-schedule">
          <Calendar className="w-4 h-4 mr-2" /> Schedule
        </TabsTrigger>
        <TabsTrigger value="lodging" className="data-[state=active]:bg-ocean data-[state=active]:text-white data-[state=active]:shadow-lg rounded-lg transition-all duration-300" data-testid="tab-lodging">
          <Hotel className="w-4 h-4 mr-2" /> Lodging
        </TabsTrigger>
        <TabsTrigger value="transport" className="data-[state=active]:bg-ocean data-[state=active]:text-white data-[state=active]:shadow-lg rounded-lg transition-all duration-300" data-testid="tab-transport">
          <Car className="w-4 h-4 mr-2" /> Transport
        </TabsTrigger>
        {!domestic && (
          <TabsTrigger value="visa" className="data-[state=active]:bg-ocean data-[state=active]:text-white data-[state=active]:shadow-lg rounded-lg transition-all duration-300" data-testid="tab-visa"
            onClick={() => fetchVisaInfo(tripInput.nationality, `${tripInput.destination_city}, ${tripInput.destination_country}`)}>
            <Globe className="w-4 h-4 mr-2" /> Visa
          </TabsTrigger>
        )}
        <TabsTrigger value="tips" className="data-[state=active]:bg-ocean data-[state=active]:text-white data-[state=active]:shadow-lg rounded-lg transition-all duration-300" data-testid="tab-tips">
          <Shield className="w-4 h-4 mr-2" /> Safety Tips & Info
        </TabsTrigger>
        <TabsTrigger value="weather" className="data-[state=active]:bg-ocean data-[state=active]:text-white data-[state=active]:shadow-lg rounded-lg transition-all duration-300" data-testid="tab-weather"
          onClick={() => fetchWeather(tripInput.destination_city, tripInput.destination_country)}>
          <Thermometer className="w-4 h-4 mr-2" /> Weather
        </TabsTrigger>
        {!domestic && (
          <TabsTrigger value="insurance" className="data-[state=active]:bg-ocean data-[state=active]:text-white data-[state=active]:shadow-lg rounded-lg transition-all duration-300" data-testid="tab-insurance"
            onClick={() => {
              const days = generatedItinerary?.days?.length || 7;
              fetchInsurance(`${tripInput.destination_city}, ${tripInput.destination_country}`, days, tripInput.adults);
            }}>
            <Heart className="w-4 h-4 mr-2" /> Insurance
          </TabsTrigger>
        )}
        <TabsTrigger value="gallery" className="data-[state=active]:bg-ocean data-[state=active]:text-white data-[state=active]:shadow-lg rounded-lg transition-all duration-300" data-testid="tab-gallery">
          <Camera className="w-4 h-4 mr-2" /> Gallery
        </TabsTrigger>
        <TabsTrigger value="companions" className="data-[state=active]:bg-ocean data-[state=active]:text-white data-[state=active]:shadow-lg rounded-lg transition-all duration-300" data-testid="tab-companions"
          onClick={() => fetchCompanions()}>
          <UserPlus className="w-4 h-4 mr-2" /> Group
        </TabsTrigger>
        <TabsTrigger value="journal" className="data-[state=active]:bg-ocean data-[state=active]:text-white data-[state=active]:shadow-lg rounded-lg transition-all duration-300" data-testid="tab-journal">
          <BookOpen className="w-4 h-4 mr-2" /> Journal
        </TabsTrigger>
      </TabsList>

      {domestic && (
        <div className="mb-4 px-4 py-2 bg-green-50 border border-green-200 rounded-xl text-sm text-green-800 flex items-center gap-2" data-testid="domestic-trip-banner">
          <Shield className="w-4 h-4" />
          <span>Domestic trip detected — Visa & Travel Insurance tabs are hidden.</span>
        </div>
      )}

      <TabsContent value="schedule">
        {renderTab('schedule', <ScheduleTab generatedItinerary={generatedItinerary} openGallery={openGallery} handleBookFlight={handleBookFlight} tripInput={tripInput} itineraryId={itineraryId} onItineraryUpdate={onItineraryUpdate} />)}
      </TabsContent>
      <TabsContent value="lodging">
        {renderTab('lodging', <LodgingTab generatedItinerary={generatedItinerary} {...currencyProps} handleBookLodging={handleBookLodging} />)}
      </TabsContent>
      <TabsContent value="transport">
        {renderTab('transport', <TransportTab generatedItinerary={generatedItinerary} {...currencyProps} />)}
      </TabsContent>
      {!domestic && (
        <TabsContent value="visa">
          {renderTab('visa', <VisaTab visaData={visaData} loadingVisa={loadingVisa} generatedItinerary={generatedItinerary} fetchVisaInfo={fetchVisaInfo} trip_input={tripInput} />)}
        </TabsContent>
      )}
      <TabsContent value="tips">
        {renderTab('tips', <TipsTab generatedItinerary={generatedItinerary} />)}
      </TabsContent>
      <TabsContent value="weather">
        {renderTab('weather', <WeatherTab weatherData={weatherData} loadingWeather={loadingWeather} tripInput={tripInput} />)}
      </TabsContent>
      {!domestic && (
        <TabsContent value="insurance">
          {renderTab('insurance', <InsuranceTab insuranceData={insuranceData} loadingInsurance={loadingInsurance} />)}
        </TabsContent>
      )}
      <TabsContent value="gallery">
        {renderTab('gallery', <GalleryTab trip_input={tripInput} generatedItinerary={generatedItinerary} />)}
      </TabsContent>
      <TabsContent value="companions">
        {renderTab('companions', <CompanionsTab companions={companions} setShowInviteDialog={setShowInviteDialog} handleRemoveCompanion={handleRemoveCompanion} itineraryId={itineraryId} />)}
      </TabsContent>
      <TabsContent value="journal">
        {renderTab('journal', <PhotoJournalTab itineraryId={itineraryId} days={generatedItinerary?.days || []} destinationCity={tripInput?.destination_city || ''} destinationCountry={tripInput?.destination_country || ''} startDate={tripInput?.start_date || ''} />)}
      </TabsContent>
    </Tabs>
  );
};
